import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MdOutlineTranslate, MdCheck } from 'react-icons/md';

/**
 * Sleek TR/EN language select box dropdown.
 * Adapts to light/dark themes automatically.
 */
export default function LanguageSwitcher({ style = {} }) {
    const { language, setLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const languages = [
        { code: 'tr', label: 'Türkçe', text: 'TR' },
        { code: 'en', label: 'English', text: 'EN' }
    ];

    const currentLang = languages.find(l => l.code === language) || languages[0];

    return (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', ...style }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                id="language-switcher-btn"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    borderRadius: 12,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                    height: 40,
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.color = 'var(--primary)';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                }}
            >
                <MdOutlineTranslate size={18} />
                <span>{language === 'tr' ? 'Dil' : 'Language'} ({currentLang.text})</span>
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    minWidth: 160,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 12,
                    boxShadow: 'var(--shadow-lg)',
                    padding: '6px',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    backdropFilter: 'blur(10px)',
                }}>
                    {languages.map(lang => (
                        <button
                            key={lang.code}
                            onClick={() => {
                                setLanguage(lang.code);
                                setIsOpen(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                padding: '10px 14px',
                                border: 'none',
                                borderRadius: 8,
                                background: language === lang.code ? 'rgba(79,142,247,0.08)' : 'transparent',
                                color: language === lang.code ? 'var(--primary)' : 'var(--text-primary)',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: language === lang.code ? 700 : 500,
                                textAlign: 'left',
                                transition: 'all 0.15s',
                            }}
                            onMouseOver={(e) => {
                                if (language !== lang.code) {
                                    e.currentTarget.style.background = 'var(--bg-elevated)';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (language !== lang.code) {
                                    e.currentTarget.style.background = 'transparent';
                                }
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    padding: '2px 6px',
                                    borderRadius: 6,
                                    background: language === lang.code ? 'var(--primary)' : 'var(--border)',
                                    color: language === lang.code ? '#ffffff' : 'var(--text-muted)',
                                }}>{lang.text}</span>
                                <span>{lang.label}</span>
                            </span>
                            {language === lang.code && <MdCheck size={16} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
